import interfaceSchema from './interface.schema.json';
import fileExplorerInterface from './interfaces/file-explorer.json';
import notepadInterface from './interfaces/notepad.json';
import mapleWordInterface from './interfaces/maple-word.json';

export type AssistantJsonSchemaType = 'object' | 'array' | 'string' | 'boolean' | 'number';

export type AssistantJsonSchema = {
  type?: AssistantJsonSchemaType;
  properties?: Record<string, AssistantJsonSchema>;
  required?: string[];
  enum?: Array<string | boolean | number>;
  items?: AssistantJsonSchema;
  additionalProperties?: boolean;
  description?: string;
};

export type AssistantActionDescriptor = {
  id: string;
  title: string;
  description: string;
  paramsSchema: AssistantJsonSchema;
  examples: string[];
  requiresConfirmation?: boolean;
  dangerous?: boolean;
};

export type AssistantInterface = {
  appId: string;
  name: string;
  description: string;
  actions: AssistantActionDescriptor[];
};

export type AssistantToolDescriptor = {
  key: string;
  appId: string;
  appName: string;
  actionId: string;
  title: string;
  description: string;
  paramSummary: string;
  requiresConfirmation: boolean;
  dangerous: boolean;
  paramsSchema: AssistantJsonSchema;
};

export type AssistantRegistryEntry = {
  app: AssistantInterface;
  action: AssistantActionDescriptor;
  tool: AssistantToolDescriptor;
};

export type AssistantRegistry = {
  interfaces: AssistantInterface[];
  tools: AssistantToolDescriptor[];
  actionsByKey: Record<string, AssistantRegistryEntry>;
};

const rawInterfaces: unknown[] = [fileExplorerInterface, notepadInterface, mapleWordInterface];
const SUPPORTED_SCHEMA_TYPES: AssistantJsonSchemaType[] = ['object', 'array', 'string', 'boolean', 'number'];

let cachedRegistry: AssistantRegistry | null = null;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const validateAgainstSchema = (value: unknown, schema: AssistantJsonSchema, path: string): string[] => {
  const errors: string[] = [];

  if (schema.type) {
    if (schema.type === 'object') {
      if (!isPlainObject(value)) {
        return [`${path} must be an object.`];
      }
    } else if (schema.type === 'array') {
      if (!Array.isArray(value)) {
        return [`${path} must be an array.`];
      }
    } else if (typeof value !== schema.type) {
      return [`${path} must be a ${schema.type}.`];
    }
  }

  if (schema.enum && !schema.enum.includes(value as string | boolean | number)) {
    errors.push(`${path} must be one of: ${schema.enum.join(', ')}.`);
  }

  if (schema.type === 'object' && isPlainObject(value)) {
    const properties = schema.properties ?? {};
    const required = schema.required ?? [];

    for (const key of required) {
      if (!(key in value)) {
        errors.push(`${path}.${key} is required.`);
      }
    }

    for (const [key, childSchema] of Object.entries(properties)) {
      if (key in value) {
        errors.push(...validateAgainstSchema(value[key], childSchema, `${path}.${key}`));
      }
    }

    if (schema.additionalProperties === false) {
      for (const key of Object.keys(value)) {
        if (!(key in properties)) {
          errors.push(`${path}.${key} is not allowed.`);
        }
      }
    }
  }

  if (schema.type === 'array' && Array.isArray(value) && schema.items) {
    value.forEach((item, index) => {
      errors.push(...validateAgainstSchema(item, schema.items as AssistantJsonSchema, `${path}[${index}]`));
    });
  }

  return errors;
};

const validateParamSchemaDefinition = (schema: AssistantJsonSchema, path: string): string[] => {
  const errors: string[] = [];

  if (!schema.type) {
    errors.push(`${path}.type is required.`);
    return errors;
  }

  if (!SUPPORTED_SCHEMA_TYPES.includes(schema.type)) {
    errors.push(`${path}.type must be one of: ${SUPPORTED_SCHEMA_TYPES.join(', ')}.`);
    return errors;
  }

  if (schema.enum && !Array.isArray(schema.enum)) {
    errors.push(`${path}.enum must be an array.`);
  }

  if (schema.required && !Array.isArray(schema.required)) {
    errors.push(`${path}.required must be an array of strings.`);
  }

  if (schema.type === 'object') {
    if (schema.properties && !isPlainObject(schema.properties)) {
      errors.push(`${path}.properties must be an object.`);
    }

    Object.entries(schema.properties ?? {}).forEach(([key, childSchema]) => {
      errors.push(...validateParamSchemaDefinition(childSchema, `${path}.properties.${key}`));
    });
  }

  if (schema.type === 'array') {
    if (!schema.items) {
      errors.push(`${path}.items is required for array schemas.`);
    } else {
      errors.push(...validateParamSchemaDefinition(schema.items, `${path}.items`));
    }
  }

  return errors;
};

const summarizePropertySchema = (schema: AssistantJsonSchema) => {
  if (schema.enum?.length) {
    return schema.enum.join('|');
  }

  return schema.type ?? 'value';
};

const summarizeParams = (schema: AssistantJsonSchema) => {
  if (schema.type !== 'object') {
    return schema.type ?? 'args';
  }

  const required = new Set(schema.required ?? []);
  const entries = Object.entries(schema.properties ?? {});

  if (entries.length === 0) {
    return 'no args';
  }

  return entries
    .map(([key, propertySchema]) => `${key}${required.has(key) ? '' : '?'}: ${summarizePropertySchema(propertySchema)}`)
    .join(', ');
};

const buildRegistry = (interfaces: AssistantInterface[]): AssistantRegistry => {
  const actionsByKey: Record<string, AssistantRegistryEntry> = {};
  const tools: AssistantToolDescriptor[] = [];

  interfaces.forEach((app) => {
    app.actions.forEach((action) => {
      const key = `${app.appId}.${action.id}`;
      const tool: AssistantToolDescriptor = {
        key,
        appId: app.appId,
        appName: app.name,
        actionId: action.id,
        title: action.title,
        description: action.description,
        paramSummary: summarizeParams(action.paramsSchema),
        requiresConfirmation: Boolean(action.requiresConfirmation),
        dangerous: Boolean(action.dangerous),
        paramsSchema: action.paramsSchema,
      };

      tools.push(tool);
      actionsByKey[key] = { app, action, tool };
    });
  });

  return { interfaces, tools, actionsByKey };
};

export async function loadAssistantInterfaces(): Promise<AssistantInterface[]> {
  if (cachedRegistry) {
    return cachedRegistry.interfaces;
  }

  const interfaces = rawInterfaces as AssistantInterface[];
  const errors: string[] = [];

  interfaces.forEach((descriptor, index) => {
    errors.push(...validateAgainstSchema(descriptor, interfaceSchema as AssistantJsonSchema, `interfaces[${index}]`));
    descriptor.actions.forEach((action, actionIndex) => {
      errors.push(...validateParamSchemaDefinition(action.paramsSchema, `interfaces[${index}].actions[${actionIndex}].paramsSchema`));
    });
  });

  if (errors.length > 0) {
    throw new Error(`Assistant interface validation failed:\n${errors.join('\n')}`);
  }

  cachedRegistry = buildRegistry(interfaces);
  return cachedRegistry.interfaces;
}

export function getAssistantRegistry(): AssistantRegistry {
  if (!cachedRegistry) {
    throw new Error('Assistant interfaces have not been loaded yet.');
  }

  return cachedRegistry;
}

export function validateToolArgs(toolKey: string, args: unknown, registry: AssistantRegistry = getAssistantRegistry()): string[] {
  const entry = registry.actionsByKey[toolKey];
  if (!entry) {
    return [`Unknown tool: ${toolKey}.`];
  }

  return validateAgainstSchema(args, entry.action.paramsSchema, 'args');
}
